import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  List,
  ListItem,
  Paper,
  InputAdornment,
  Tooltip,
  Divider,
  DialogContentText
} from '@mui/material';
import {
  VideoCall as VideoCallIcon,
  Add as AddIcon,
  Launch as LaunchIcon,
  Schedule as ScheduleIcon,
  People as PeopleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const ALLOWED_ROLES = [
  'hr',
  'hr-manager',
  'project-manager',
  'product-manager',
  'product-owner',
  'project-owner',
  'cpo',
  'admin'
];

export default function Meetings() {
  const [meetings, setMeetings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [openMeetingDialog, setOpenMeetingDialog] = useState(false);
  const [editingMeetingId, setEditingMeetingId] = useState(null);

  // Form state
  const [meetingForm, setMeetingForm] = useState({
    title: '',
    time: '',
    date: '',
    link: '',
    attendees: []
  });

  // Search filter for attendees
  const [attendeeSearch, setAttendeeSearch] = useState('');

  // Delete dialog state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [meetingToDelete, setMeetingToDelete] = useState(null);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);

  // Get user info from token or localStorage
  useEffect(() => {
    const getUserFromToken = () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const base64Url = token.split('.')[1];
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const jsonPayload = decodeURIComponent(
            atob(base64)
              .split('')
              .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
              .join('')
          );
          const decoded = JSON.parse(jsonPayload);
          setUser(decoded);
        } catch {
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              setUser(JSON.parse(storedUser));
            } catch {
              setUser(null);
            }
          }
        }
      }
    };

    getUserFromToken();
  }, []);

  // Check if user has permission to manage meetings
  const canManageMeetings = useMemo(() => {
    if (!user || !user.role) return false;
    return ALLOWED_ROLES.includes(user.role.toLowerCase());
  }, [user]);

  // Fetch meetings and employees on mount / user change
  useEffect(() => {
    if (user) {
      fetchMeetings();
      if (canManageMeetings) {
        fetchEmployees();
      }
      const token = localStorage.getItem('token');
      if (user.company_id && token) {
        axios
          .get(`${API_URL}/api/cmp/${user.company_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
          .then(res => {
            if (res.data && res.data.data) {
              setCompanyConfig(res.data.data);
            }
          })
          .catch(err =>
            {

            //  console.error('Error fetching company config:', err)
      });
      }
    }
  }, [user, canManageMeetings]);

  const fetchMeetings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setLoading(true);
      const response = await fetch(`${API_URL}/api/meetings`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setMeetings(data.meetings || []);
      } else {
        setError('Failed to fetch meetings');
      }
    } catch {
      setError('Network error while fetching meetings');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch(`${API_URL}/api/addteam/employees`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.employees || data || []);
      }
    } catch (err) {
      // console.error('Error fetching employees:', err);
    }
  };

  // Filtered employees list based on search term
  const filteredEmployees = useMemo(() => {
    if (!attendeeSearch.trim()) return employees;
    const q = attendeeSearch.toLowerCase().trim();
    return employees.filter(employee => {
      const name = (
        employee.name ||
        `${employee.first_name || ''} ${employee.last_name || ''}`
      ).toLowerCase();
      const email = (employee.email || '').toLowerCase();
      const empId = String(employee.employee_id || employee.id || '').toLowerCase();
      return name.includes(q) || email.includes(q) || empId.includes(q);
    });
  }, [employees, attendeeSearch]);

  // "Select All" status for current filtered list
  const areAllFilteredSelected = useMemo(() => {
    if (filteredEmployees.length === 0) return false;
    return filteredEmployees.every(emp => {
      const id = emp.employee_id || emp.id;
      return meetingForm.attendees.includes(id);
    });
  }, [filteredEmployees, meetingForm.attendees]);

  const isSomeFilteredSelected = useMemo(() => {
    if (filteredEmployees.length === 0) return false;
    const hasSome = filteredEmployees.some(emp => {
      const id = emp.employee_id || emp.id;
      return meetingForm.attendees.includes(id);
    });
    return hasSome && !areAllFilteredSelected;
  }, [filteredEmployees, meetingForm.attendees, areAllFilteredSelected]);

  const handleToggleSelectAll = () => {
    const filteredIds = filteredEmployees.map(emp => emp.employee_id || emp.id);
    if (areAllFilteredSelected) {
      // Deselect all currently filtered employees
      setMeetingForm(prev => ({
        ...prev,
        attendees: prev.attendees.filter(id => !filteredIds.includes(id))
      }));
    } else {
      // Select all currently filtered employees (union)
      setMeetingForm(prev => ({
        ...prev,
        attendees: Array.from(new Set([...prev.attendees, ...filteredIds]))
      }));
    }
  };

  const handleAttendeeToggle = employeeId => {
    setMeetingForm(prev => ({
      ...prev,
      attendees: prev.attendees.includes(employeeId)
        ? prev.attendees.filter(id => id !== employeeId)
        : [...prev.attendees, employeeId]
    }));
  };

  const handleOpenAddDialog = () => {
    setMeetingForm({
      title: '',
      time: '',
      date: '',
      link: '',
      attendees: []
    });
    setEditingMeetingId(null);
    setAttendeeSearch('');
    setError('');
    setOpenMeetingDialog(true);
  };

  const handleOpenEditDialog = meeting => {
    let attendeeIds = [];
    if (Array.isArray(meeting.MeetingAttendees) && meeting.MeetingAttendees.length > 0) {
      attendeeIds = meeting.MeetingAttendees.map(a => a.employeeId || a.employee_id);
    } else if (Array.isArray(meeting.attendees)) {
      attendeeIds = meeting.attendees;
    }

    let formattedDate = meeting.date || '';
    if (formattedDate.includes('T')) {
      formattedDate = formattedDate.split('T')[0];
    }

    let formattedTime = meeting.time || '';
    if (formattedTime.length > 5) {
      formattedTime = formattedTime.substring(0, 5);
    }

    setMeetingForm({
      title: meeting.title || '',
      time: formattedTime,
      date: formattedDate,
      link: meeting.link || '',
      attendees: attendeeIds
    });
    setEditingMeetingId(meeting.id);
    setAttendeeSearch('');
    setError('');
    setOpenMeetingDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenMeetingDialog(false);
    setEditingMeetingId(null);
    setMeetingForm({ title: '', time: '', date: '', link: '', attendees: [] });
    setAttendeeSearch('');
    setError('');
  };

  const handleSaveMeeting = async () => {
    if (!meetingForm.title || !meetingForm.time || !meetingForm.date || !meetingForm.link) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No authentication token found. Please log in.');
        return;
      }

      if (editingMeetingId) {
        // Update existing meeting
        const response = await axios.put(
          `${API_URL}/api/meetings/${editingMeetingId}`,
          meetingForm,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        const updated = response.data.meeting;
        setMeetings(prev => prev.map(m => (m.id === editingMeetingId ? updated : m)));
      } else {
        // Create new meeting
        const response = await axios.post(
          `${API_URL}/api/meetings`,
          meetingForm,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        setMeetings(prev => [...prev, response.data.meeting]);
      }

      handleCloseDialog();
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
      } else {
        setError('An error occurred while saving the meeting');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenDeleteDialog = meeting => {
    setMeetingToDelete(meeting);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setMeetingToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!meetingToDelete) return;
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/meetings/${meetingToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMeetings(prev => prev.filter(m => m.id !== meetingToDelete.id));
      handleCloseDeleteDialog();
    } catch (err) {
      // console.error('Error deleting meeting:', err);
      setError(err.response?.data?.message || 'Failed to delete meeting');
      handleCloseDeleteDialog();
    } finally {
      setDeleting(false);
    }
  };

  const formatDateTime = (dateString, timeString) => {
    try {
      let combinedDateTime;
      if (dateString.includes('T')) {
        const datePart = new Date(dateString);
        if (timeString) {
          const [hours, minutes] = timeString.split(':');
          datePart.setHours(hours || 0, minutes || 0);
        }
        combinedDateTime = datePart;
      } else {
        combinedDateTime = new Date(`${dateString}T${timeString}`);
      }

      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      };
      if (companyConfig?.timezone) {
        options.timeZone = companyConfig.timezone;
      }
      return combinedDateTime.toLocaleString('en-US', options);
    } catch {
      return `${dateString} ${timeString}`;
    }
  };

  if (!user) {
    return (
      <Card
        sx={{
          height: 450,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'grey.300',
          boxShadow: 2
        }}
      >
        <CircularProgress />
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: 450,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: 'grey.300',
        boxShadow: 2,
        overflow: 'hidden'
      }}
    >
      {/* Sticky Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          py: 2,
          borderBottom: '1px solid #eee',
          bgcolor: 'background.paper',
          flexShrink: 0
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          component="h2"
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <VideoCallIcon sx={{ mr: 1, color: '#9c27b0', fontSize: 32 }} />
          Meetings
        </Typography>

        {canManageMeetings && (
          <Tooltip title="Add New Meeting" arrow>
            <IconButton
              onClick={handleOpenAddDialog}
              disabled={loading}
              sx={{
                bgcolor: '#9c27b0',
                color: 'white',
                '&:hover': { bgcolor: '#7b1fa2' }
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Scrollable Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          px: 3,
          py: 2,
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#c084fc',
            borderRadius: '4px',
            '&:hover': {
              background: '#a855f7'
            }
          },
          scrollbarWidth: 'thin',
          scrollbarColor: '#c084fc #f1f1f1'
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {loading && meetings.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Loading meetings...
            </Typography>
          </Box>
        ) : meetings.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <VideoCallIcon sx={{ fontSize: 48, color: 'grey.300', mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              No meetings scheduled
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {meetings.map(meeting => (
              <ListItem
                key={meeting.id}
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.200',
                  borderRadius: 2,
                  mb: 1.5,
                  bgcolor: 'background.paper',
                  display: 'block',
                  p: 2,
                  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                  '&:hover': {
                    borderColor: '#ce93d8',
                    boxShadow: '0 2px 8px rgba(156, 39, 176, 0.08)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, mr: 1 }}>
                    <VideoCallIcon sx={{ mr: 1.5, color: '#9c27b0', mt: 0.5 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {meeting.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}
                      >
                        <ScheduleIcon sx={{ fontSize: 14, mr: 0.5 }} />
                        {formatDateTime(meeting.date, meeting.time)}
                      </Typography>

                      {meeting.MeetingAttendees && meeting.MeetingAttendees.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, mb: 1 }}>
                          <PeopleIcon sx={{ fontSize: 15, mr: 0.5, color: 'grey.600' }} />
                          <Typography variant="caption" color="text.secondary">
                            {meeting.MeetingAttendees.length} Attendee(s)
                          </Typography>
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<LaunchIcon />}
                          onClick={() => window.open(meeting.link, '_blank')}
                          sx={{
                            borderColor: '#9c27b0',
                            color: '#9c27b0',
                            textTransform: 'none',
                            fontWeight: 500,
                            '&:hover': {
                              borderColor: '#7b1fa2',
                              backgroundColor: 'rgba(156, 39, 176, 0.04)'
                            }
                          }}
                        >
                          Join Meeting
                        </Button>

                        {canManageMeetings && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Edit Meeting" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditDialog(meeting)}
                                sx={{
                                  color: '#7b1fa2',
                                  bgcolor: 'rgba(156, 39, 176, 0.08)',
                                  '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.16)' }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete Meeting" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDeleteDialog(meeting)}
                                sx={{
                                  color: '#d32f2f',
                                  bgcolor: 'rgba(211, 47, 47, 0.08)',
                                  '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.16)' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Add / Edit Meeting Dialog */}
      <Dialog
        open={openMeetingDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1.5 }}>
          <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
            {editingMeetingId ? (
              <>
                <EditIcon sx={{ mr: 1, color: '#9c27b0' }} />
                Edit Meeting
              </>
            ) : (
              <>
                <AddIcon sx={{ mr: 1, color: '#9c27b0' }} />
                Add New Meeting
              </>
            )}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {error && (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <TextField
              label="Meeting Title"
              required
              fullWidth
              size="small"
              value={meetingForm.title}
              onChange={e => setMeetingForm({ ...meetingForm, title: e.target.value })}
              placeholder="e.g. Sprint Planning, Client Sync"
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Date"
                type="date"
                required
                fullWidth
                size="small"
                value={meetingForm.date}
                onChange={e => setMeetingForm({ ...meetingForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                label="Time"
                type="time"
                required
                fullWidth
                size="small"
                value={meetingForm.time}
                onChange={e => setMeetingForm({ ...meetingForm, time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            <TextField
              label="Meeting Link"
              type="url"
              required
              fullWidth
              size="small"
              value={meetingForm.link}
              onChange={e => setMeetingForm({ ...meetingForm, link: e.target.value })}
              placeholder="https://meet.google.com/... or Teams / Zoom"
            />

            {/* Attendees Selection Section */}
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
                  <PeopleIcon sx={{ mr: 0.75, fontSize: 18, color: '#9c27b0' }} />
                  Select Attendees
                </Typography>
                <Chip
                  label={`${meetingForm.attendees.length} selected`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ borderColor: '#9c27b0', color: '#9c27b0', fontWeight: 600 }}
                />
              </Box>

              {/* Attendee Search Input */}
              <TextField
                placeholder="Search employees by name, email, or ID..."
                size="small"
                fullWidth
                value={attendeeSearch}
                onChange={e => setAttendeeSearch(e.target.value)}
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'grey.500' }} />
                    </InputAdornment>
                  ),
                  endAdornment: attendeeSearch ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setAttendeeSearch('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />

              {/* Select All Bar */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  px: 1,
                  py: 0.5,
                  bgcolor: '#f8f4fb',
                  borderRadius: 1,
                  border: '1px solid #e1bee7',
                  mb: 1
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={areAllFilteredSelected}
                      indeterminate={isSomeFilteredSelected}
                      onChange={handleToggleSelectAll}
                      sx={{
                        color: '#9c27b0',
                        '&.Mui-checked': { color: '#9c27b0' },
                        '&.MuiCheckbox-indeterminate': { color: '#9c27b0' }
                      }}
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#4a148c' }}>
                      Select All {filteredEmployees.length > 0 ? `(${filteredEmployees.length})` : ''}
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />

                {meetingForm.attendees.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => setMeetingForm(prev => ({ ...prev, attendees: [] }))}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#d32f2f' }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>

              {/* Employees List Container */}
              <Paper
                variant="outlined"
                sx={{
                  maxHeight: 180,
                  overflowY: 'auto',
                  p: 0.5,
                  bgcolor: '#fafafa',
                  borderColor: 'grey.300',
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-thumb': { background: '#ce93d8', borderRadius: '3px' }
                }}
              >
                {filteredEmployees.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                    {employees.length === 0 ? 'Loading employees...' : 'No employees matching your search'}
                  </Typography>
                ) : (
                  filteredEmployees.map(employee => {
                    const empId = employee.employee_id || employee.id;
                    const isChecked = meetingForm.attendees.includes(empId);
                    const displayName =
                      employee.name ||
                      `${employee.first_name || ''} ${employee.last_name || ''}`.trim() ||
                      employee.email;

                    return (
                      <FormControlLabel
                        key={empId}
                        control={
                          <Checkbox
                            size="small"
                            checked={isChecked}
                            onChange={() => handleAttendeeToggle(empId)}
                            sx={{
                              color: '#9c27b0',
                              '&.Mui-checked': { color: '#9c27b0' }
                            }}
                          />
                        }
                        label={
                          <Box sx={{ py: 0.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: isChecked ? 600 : 400 }}>
                              {displayName}
                            </Typography>
                            {employee.email && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {employee.email}
                              </Typography>
                            )}
                          </Box>
                        }
                        sx={{
                          display: 'flex',
                          width: '100%',
                          m: 0,
                          px: 1,
                          py: 0.25,
                          borderRadius: 1,
                          bgcolor: isChecked ? 'rgba(156, 39, 176, 0.05)' : 'transparent',
                          '&:hover': { bgcolor: 'rgba(156, 39, 176, 0.08)' }
                        }}
                      />
                    );
                  })
                )}
              </Paper>
            </Box>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveMeeting}
            variant="contained"
            disabled={!meetingForm.title || !meetingForm.time || !meetingForm.date || !meetingForm.link || submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : editingMeetingId ? <EditIcon /> : <AddIcon />}
            sx={{
              bgcolor: '#9c27b0',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { bgcolor: '#7b1fa2' },
              '&:disabled': { bgcolor: '#e1bee7', color: 'white' }
            }}
          >
            {submitting ? (editingMeetingId ? 'Updating...' : 'Creating...') : editingMeetingId ? 'Update Meeting' : 'Add Meeting'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Meeting Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#d32f2f', display: 'flex', alignItems: 'center' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Meeting
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the meeting <strong>"{meetingToDelete?.title}"</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDeleteDialog} color="inherit" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}